import React, { useState, useEffect } from 'react';
import {
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  Text,
  Box,
  Spinner,
  Link,
  Badge,
  Divider,
  Layout,
} from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import api from '../utils/axiosConfig';

function ZyzfyEmailSettings() {
  const { t } = useTranslation();
  const [zyzfyEmail, setZyzfyEmail] = useState('');
  const [savedEmail, setSavedEmail] = useState('');
  const [loading, setLoading] = useState(false); // Don't show loading spinner
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState(null);

  useEffect(() => {
    loadZyzfyEmailSettings();
  }, []);

  const loadZyzfyEmailSettings = async () => {
    try {
      // Fetch current settings quietly
      const response = await api.get('/settings/zyzfy-email');
      
      if (response.data.zyzfyEmail) {
        setZyzfyEmail(response.data.zyzfyEmail);
        setSavedEmail(response.data.zyzfyEmail);
      }
      
      setIntegrationStatus(response.data.integrationStatus);
    } catch (error) {
      // Silent error handling
      console.error('Error loading settings:', error);
    }
  };

  const saveZyzfyEmail = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess(false);

      // Validate email format
      if (!isValidEmail(zyzfyEmail)) {
        setError('Email inválido');
        return;
      }

      // Save Zyzfy email to backend
      const response = await api.post('/settings/zyzfy-email', {
        zyzfyEmail: zyzfyEmail.trim().toLowerCase()
      });

      if (response.data.success) {
        setSavedEmail(zyzfyEmail);
        setSuccess(true);
        setIntegrationStatus(response.data.integrationStatus);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
        
        // If integration was activated, show special notification
        if (response.data.integrationActivated) {
          setSuccess('integration_activated');
        }
      }
    } catch (error) {
      console.error('Error saving email:', error);
      setError(error.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (value) => {
    setZyzfyEmail(value);
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const hasChanges = zyzfyEmail !== savedEmail;

  if (loading) {
    return (
      <Card>
        <Box padding="400">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Spinner size="large" />
            <Text variant="bodyMd" as="p" color="subdued">
              Carregando configurações...
            </Text>
          </div>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <Box padding="400">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <Text variant="headingSm" as="h4">
              Configuração Avançada
            </Text>
            <Text variant="bodySm" as="p" color="subdued">
              Integração com o sistema Zyzfy
            </Text>
          </div>

          <Divider />

          {/* Integration Status - More subtle */}
          {integrationStatus === 'active' && (
            <Box paddingBlockEnd="200">
              <div style={{ padding: '0.5rem', backgroundColor: '#f0fdf4', borderRadius: '4px' }}>
                <Text variant="bodySm" as="p" color="success">
                  Configuração sincronizada
                </Text>
              </div>
            </Box>
          )}

          {/* Success Message - Subtle */}
          {success && (
            <Box paddingBlockEnd="200">
              <div style={{ padding: '0.5rem', backgroundColor: '#f0fdf4', borderRadius: '4px' }}>
                <Text variant="bodySm" as="p" color="success">
                  {success === 'integration_activated' ? 'Sincronização ativada' : 'Configuração salva'}
                </Text>
              </div>
            </Box>
          )}

          {/* Error Message - Subtle */}
          {error && (
            <Box paddingBlockEnd="200">
              <div style={{ padding: '0.5rem', backgroundColor: '#fef2f2', borderRadius: '4px' }}>
                <Text variant="bodySm" as="p" color="critical">
                  {error}
                </Text>
              </div>
            </Box>
          )}

          {/* Email Input Form - Discreet */}
          <FormLayout>
            <TextField
              label="Email de sincronização"
              value={zyzfyEmail}
              onChange={handleEmailChange}
              type="email"
              autoComplete="email"
              helpText="Email para integração com o sistema Zyzfy"
              placeholder="email@exemplo.com"
              error={error && !isValidEmail(zyzfyEmail) && zyzfyEmail ? 'Email inválido' : undefined}
            />
          </FormLayout>

          {/* Action Button - Discreet */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="slim"
              onClick={saveZyzfyEmail}
              loading={saving}
              disabled={!hasChanges || !zyzfyEmail}
            >
              Salvar
            </Button>
          </div>


        </div>
      </Box>
    </Card>
  );
}

export default ZyzfyEmailSettings;